import type { Editor } from '@tiptap/core'
import {
  findThreadsInDocument,
  type ThreadOptions,
} from '@tiptap-pro/extension-comments'

// `findThreadsInDocument` reports a thread as its id, not as the thread object,
// so resolution state has to be looked up on the provider. The provider lives on
// the `comments` extension's options, which tiptap does not type per extension.
const threadOptions = (editor: Editor): Partial<ThreadOptions> =>
  (editor.extensionManager.extensions.find(({ name }) => name === 'comments')
    ?.options as ThreadOptions | undefined) ?? {}

/**
 * Whether a range overlaps a comment thread that is still open.
 *
 * Resolved threads keep their marks in the document but are not surfaced
 * anywhere, so their text is treated as free to comment on again.
 *
 * A thread the provider does not know about counts as open, matching how the
 * extension colours its own decorations: resolution has to be proved, never
 * assumed.
 */
export const hasUnresolvedThread = (
  editor: Editor,
  { from, to }: { from: number; to: number }
): boolean => {
  const { provider, blockThreadTypeName, inlineThreadTypeName } =
    threadOptions(editor)

  const resolvedIds = new Set(
    (provider?.getThreads() ?? [])
      .filter(({ resolvedAt }) => resolvedAt)
      .map(({ id }) => String(id))
  )

  return findThreadsInDocument(editor, {
    blockTypeName: blockThreadTypeName,
    markTypeName: inlineThreadTypeName,
  }).some(
    ({ pos, node, thread }) =>
      // Half-open overlap: a thread that merely abuts the range is outside it.
      pos < to && pos + node.nodeSize > from && !resolvedIds.has(String(thread))
  )
}
